import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Row, Col, Input, Select, Button, Alert, Empty, Modal } from 'antd';
import { ThunderboltOutlined, PlusOutlined, DeleteOutlined, InfoCircleOutlined, SaveOutlined } from '@ant-design/icons';
import { apiFetch } from '../utils/api.js';

// ===== 糖果风设计系统 =====
const CORAL = '#D85A30';
const RED = '#f5222d';
const GREEN = '#52c41a';
const INK = '#26201C';        // 主文字
const MUTED = '#A08C82';      // 次要文字
const CREAM = '#FFF8F4';      // 页面奶油底
const CORAL_BG = '#FFF1EA';   // 珊瑚浅底
const BORDER = '#F3E2D8';     // 暖色描边
const TONE = {
  success: { bg: 'linear-gradient(135deg,#EDF9F1,#F8FDFA)', bd: '#BEE6CC', fg: '#237A46', chip: '可行', chipBg: '#E7F6EE' },
  warning: { bg: 'linear-gradient(135deg,#FFF6E3,#FFFCF4)', bd: '#F2DBA6', fg: '#A06A00', chip: '注意', chipBg: '#FDF3DC' },
  error:   { bg: 'linear-gradient(135deg,#FFEEEA,#FFF6F4)', bd: '#F4C3BA', fg: '#C22E1F', chip: '风险', chipBg: '#FDECEA' },
};

// 金额格式化：大额转「万」
const fmtMoney = (v) => {
  if (v == null || isNaN(v)) return '—';
  const n = Number(v);
  const abs = Math.abs(n);
  if (abs >= 10000) return (n / 10000).toFixed(2) + '万';
  return n.toLocaleString('zh-CN', { maximumFractionDigits: 0 });
};
const fmt2 = (v) => (v == null || isNaN(v)) ? '—' : Number(v).toLocaleString('zh-CN', { maximumFractionDigits: 2 });
const fmtPct = (v) => (v == null || isNaN(v)) ? '—' : (Number(v) * 100).toFixed(2) + '%';
const r2 = (v) => Math.round(v * 100) / 100;
const r1 = (v) => Math.round(v * 10) / 10;
const clamp01 = (v) => Math.max(0, Math.min(1, v));

/**
 * 核心测算（Accoi 一次性牙刷测算模型 v7，融合进打爆款口径）
 * 单笔净利 = 售价×(1-退款率)×(1-平台费率) − 每单固定推广费
 *          − (1−退款率)×(货品成本+运费)
 *          − 退款率×[货品成本×(1−退货可再售ρ) + 2×运费×(1−运费申诉回收σ)]
 *          − 辅材
 * v7 口径：推广费 = 每单固定金额 promoFee（元/单），直接从净到手里减（退款单照扣），0=自然流。
 * 阶段支持各自运费（单量阶梯不同快递单价不同，留空=跟随全局运费）。
 * 下游：月净现金流（含固定成本摊薄）、回本月数、保本临界值、资金生命线、半付费混合记账。
 */
function computePlan(unit, opts, stages) {
  const price = +unit.price || 0;
  const cost = +unit.cost || 0;
  const freight = +unit.freight || 0;
  const packaging = +unit.packaging || 0;
  const platformRate = (+unit.platformRate || 0) / 100;
  const refundRate = (+unit.refundRate || 0) / 100;
  const resalable = clamp01((+unit.resalable || 0) / 100);
  const shipRecovery = clamp01((+unit.shipRecovery || 0) / 100);
  const fixedCost = +opts.fixedCost || 0;
  const days = Math.max(1, +opts.days || 30);
  const stock = +opts.stock || 0;
  const otherUpfront = +opts.otherUpfront || 0;
  const cash = +opts.cash || 0;
  const lagDays = Math.max(0, +opts.lagDays || 0);
  const targetDailyOrders = Math.max(0, +opts.targetDailyOrders || 0);

  const denom = (1 - refundRate) * (1 - platformRate);
  // 退款单残余损失（不可回收的货 + 两程不可回收的运费），按运费 f 参数化（分段运费）
  const residOf = (f) => refundRate * (cost * (1 - resalable) + 2 * f * (1 - shipRecovery));
  const uCostOf = (f) => (1 - refundRate) * (cost + f);

  // v7：给定「每单固定推广费(元)」+ 运费，求单笔净利（推广费直接从净到手减，退款单照扣）
  const perOrderNet = (adPer, f = freight) =>
    price * denom - Math.max(0, adPer) - uCostOf(f) - residOf(f) - packaging;

  let cum = 0, minCum = 0;
  const cumPoints = [];
  const stageRows = [];
  let totalAd = 0;
  for (const s of stages) {
    const d = Math.max(0, Math.round(+s.days || 0));
    const dq = Math.max(0, +s.dailyOrders || 0);
    const adPer = Math.max(0, +s.adPer || 0);
    const sFreight = (s.freight == null || s.freight === '' || isNaN(+s.freight)) ? freight : Math.max(0, +s.freight);
    const netPer = perOrderNet(adPer, sFreight);
    const dailyNet = dq * netPer - fixedCost / days;
    const dailyAd = dq * adPer;
    totalAd += dailyAd * d;
    stageRows.push({
      key: stageRows.length, name: s.name || ('阶段' + (stageRows.length + 1)), days: d, dailyOrders: dq,
      adPer, freight: sFreight, netPer, dailyNet, stageAd: dailyAd * d, stageNet: dailyNet * d,
    });
    for (let i = 0; i < d; i++) { cum += dailyNet; if (cum < minCum) minCum = cum; cumPoints.push(cum); }
  }

  // 稳态（守位期）
  const last = stageRows[stageRows.length - 1];
  const steadyAd = last ? last.adPer : 0;
  const steadyFreight = last ? last.freight : freight;
  const steadyNetPer = perOrderNet(steadyAd, steadyFreight);
  const steadyDailyOrders = last ? last.dailyOrders : targetDailyOrders;
  const steadyDailyNet = steadyDailyOrders * steadyNetPer - fixedCost / days;
  const steadyMonthly = steadyDailyNet * days;

  // 单笔拆解（守位期口径，供透明展示）
  const steadyNetIn = price * denom - steadyAd;
  const unitCost = uCostOf(steadyFreight);
  const refundResidual = residOf(steadyFreight);

  // 资金生命线（满负荷垫资口径）
  const upfront = stock + otherUpfront;
  const dailyFloat = targetDailyOrders * (cost + steadyFreight + packaging + steadyAd);
  const inTransit = dailyFloat * lagDays;
  const cashNeeded = upfront + inTransit;
  const freeCash = cash - cashNeeded;
  const paybackMonths = steadyMonthly > 0 ? upfront / steadyMonthly : null;   // null = 永不回本
  const daysToZero = steadyMonthly < 0 ? (freeCash / -steadyMonthly) * days : null; // null = 持续为正

  // 保本临界值（精确自洽：含辅材 + 退款残余；对应 v7 的 maxPromoFee / minPrice）
  const base = unitCost + refundResidual + packaging;
  const maxAdPer = denom > 0 ? price * denom - base : null;                    // 保本最高每单推广费（v7 maxPromoFee）
  const maxPromoRate = price > 0 && maxAdPer != null ? maxAdPer / price : null; // 保本最高推广费率
  const minPrice = denom > 0 ? (base + steadyAd) / denom : null;               // 保本最低售价

  // 现金流曲线（含前期一次性投入 upfront），回本 = 累计现金流 ≥ upfront 的第一天
  const cashPoints = [-upfront, ...cumPoints.map(c => c - upfront)];
  let paybackDays = null;
  for (let i = 0; i < cumPoints.length; i++) {
    if (cumPoints[i] >= upfront) { paybackDays = i + 1; break; }
  }

  return {
    steadyNetPer, steadyDailyNet, steadyMonthly, steadyNetIn, unitCost, refundResidual, packaging,
    totalAd, upfront, inTransit, cashNeeded, freeCash, daysToZero, paybackMonths,
    maxAdPer, maxPromoRate, minPrice, paybackDays, stageRows, cashPoints, cumPoints,
  };
}

function scenario(unit, opts, stages, adScale) {
  const scaled = stages.map(s => ({ ...s, adPer: (s.adPer || 0) * adScale }));
  const r = computePlan(unit, opts, scaled);
  return { scale: adScale, totalAd: r.totalAd, steadyMonthly: r.steadyMonthly, paybackMonths: r.paybackMonths };
}

/**
 * 7 天停投测试判定（Accoi sevenDayTest）
 * 前 3 天付费点火 → 第 4 天起完全停投 → 停投后每天自然单数按顺序传入。
 */
function sevenDayTest(ordersArr, unit, opts) {
  const price = +unit.price || 0, cost = +unit.cost || 0, freight = +unit.freight || 0, packaging = +unit.packaging || 0;
  const platformRate = (+unit.platformRate || 0) / 100, refundRate = (+unit.refundRate || 0) / 100;
  const resalable = clamp01((+unit.resalable || 0) / 100), shipRecovery = clamp01((+unit.shipRecovery || 0) / 100);
  const fixedCost = +opts.fixedCost || 0, days = Math.max(1, +opts.days || 30);

  const list = (ordersArr || []).map(Number).filter(v => !isNaN(v) && v >= 0);
  const n = list.length;
  const avgDaily = n ? list.reduce((a, b) => a + b, 0) / n : 0;

  const netIn0 = price * ((1 - refundRate) * (1 - platformRate));
  const unitCost0 = (1 - refundRate) * (cost + freight);
  const refundResidual0 = refundRate * (cost * (1 - resalable) + 2 * freight * (1 - shipRecovery));
  const profitZero = netIn0 - unitCost0 - refundResidual0 - packaging;

  const breakEvenDaily = profitZero > 0 ? fixedCost / profitZero / days : Infinity;
  const targetDaily = Math.round(breakEvenDaily * (1 + 0.15));
  const passed = profitZero > 0 && avgDaily >= targetDaily;

  let minPriceForVolume = null;
  if (!passed && avgDaily > 0) {
    const needPerOrder = fixedCost / (avgDaily * days);
    minPriceForVolume = (needPerOrder + (1 - refundRate) * (cost + freight) + refundResidual0 + packaging) /
      ((1 - refundRate) * (1 - platformRate));
  }

  let suggestion;
  if (passed) {
    suggestion = '通过：停投后自然单已覆盖固定成本，可进入备货。';
  } else if (avgDaily >= breakEvenDaily) {
    suggestion = '自然单已过保本线，但低于 15% 安全垫，建议再观察几天或小幅提价。';
  } else if (minPriceForVolume != null) {
    suggestion = `自然单量撑不住：当前日均 ${r2(avgDaily)} 单，需把售价提到 ¥${r2(minPriceForVolume)} 以上，或继续优化自然流。`;
  } else {
    suggestion = '停投后几乎没有自然单：先优化标题/主图/转化，或测试提价到 125~130 元再测。';
  }

  return { passed, avgDaily, breakEvenDaily, targetDaily, minPriceForVolume, verdict: passed ? '通过' : '未通过', suggestion };
}

/**
 * 半付费混合记账（Accoi v7 dailyResult）：付费单每单扣 promoFee，自然单不扣，分开算每天的账。
 */
function dailyMixResult(mix, unit, opts) {
  const price = +unit.price || 0, cost = +unit.cost || 0, freight = +unit.freight || 0, packaging = +unit.packaging || 0;
  const platformRate = (+unit.platformRate || 0) / 100, refundRate = (+unit.refundRate || 0) / 100;
  const resalable = clamp01((+unit.resalable || 0) / 100), shipRecovery = clamp01((+unit.shipRecovery || 0) / 100);
  const fixedCost = +opts.fixedCost || 0, days = Math.max(1, +opts.days || 30);
  const paidOrders = Math.max(0, +mix.paid || 0), naturalOrders = Math.max(0, +mix.natural || 0);
  const promoFee = Math.max(0, +mix.promoFee || 0);

  const denom = (1 - refundRate) * (1 - platformRate);
  const unitCost = (1 - refundRate) * (cost + freight);
  const refundResidual = refundRate * (cost * (1 - resalable) + 2 * freight * (1 - shipRecovery));
  const naturalNet = price * denom - unitCost - refundResidual - packaging; // 自然单单笔净利（推广费 0）
  const paidNet = naturalNet - promoFee;                                    // 付费单单笔净利

  const gross = naturalOrders * naturalNet + paidOrders * paidNet;
  const dailyFixed = fixedCost / days;
  const breakEvenNaturalDaily = naturalNet > 0 ? fixedCost / naturalNet / days : Infinity;
  return {
    naturalNet, paidNet,
    totalOrders: paidOrders + naturalOrders,
    gross, net: gross - dailyFixed,
    breakEvenNaturalDaily,
    naturalShortfall: breakEvenNaturalDaily === Infinity ? null : Math.max(0, breakEvenNaturalDaily - naturalOrders),
  };
}

// ===== 糖果风基础组件 =====
const NUM_RE = /^-?\d*\.?\d*$/;
// [v8] type="text" + defaultValue（uncontrolled）+ 正则校验（规避 number input 受控中间态隐患）
function NumInput({ value, onChange, step = 1, min, max, addonAfter, style, placeholder }) {
  const handle = (raw) => {
    if (raw !== '' && !NUM_RE.test(raw)) return;
    if (raw === '' || raw === '-' || raw === '.' || raw === '-.') { onChange(undefined); return; }
    const n = Number(raw);
    if (!isNaN(n)) onChange(n);
  };
  return (
    <div style={{ display: 'flex', alignItems: 'stretch', width: '100%', ...style }}>
      <input
        type="text"
        inputMode="decimal"
        autoComplete="off"
        defaultValue={value == null || isNaN(value) ? '' : String(value)}
        placeholder={placeholder}
        onChange={e => handle(e.target.value)}
        style={{
          flex: 1, minWidth: 0, padding: '7px 10px', border: '1px solid #EBD9CE',
          borderRadius: addonAfter ? '9px 0 0 9px' : 9,
          fontSize: 13, outline: 'none', background: '#FFFDFB', color: INK,
        }}
      />
      {addonAfter && (
        <span style={{
          display: 'inline-flex', alignItems: 'center', padding: '0 9px',
          border: '1px solid #EBD9CE', borderLeft: 0, borderRadius: '0 9px 9px 0',
          background: CORAL_BG, color: CORAL, fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap',
        }}>{addonAfter}</span>
      )}
    </div>
  );
}

// 糖果风分区卡片：珊瑚色序号徽章 + 标题 + 副标题
function CandyCard({ no, title, sub, extra, children, style }) {
  return (
    <div style={{
      background: '#fff', borderRadius: 16, padding: '16px 18px', marginBottom: 16,
      border: '1px solid ' + BORDER, boxShadow: '0 2px 12px rgba(216,90,48,0.06)',
      display: 'flex', flexDirection: 'column', ...style,
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {no && (
            <span style={{
              width: 26, height: 26, borderRadius: 9, background: 'linear-gradient(135deg,#E06B3F,#C94E26)',
              color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 13, fontWeight: 800, boxShadow: '0 2px 6px rgba(216,90,48,0.35)', flexShrink: 0,
            }}>{no}</span>
          )}
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: INK, lineHeight: 1.3 }}>{title}</div>
            {sub && <div style={{ fontSize: 12, color: MUTED, marginTop: 3 }}>{sub}</div>}
          </div>
        </div>
        {extra}
      </div>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>{children}</div>
    </div>
  );
}

// 大指标卡
function StatCard({ title, value, color, sub }) {
  return (
    <div style={{
      background: '#fff', borderRadius: 14, padding: '13px 16px', flex: 1, minWidth: 0,
      border: '1px solid ' + BORDER, boxShadow: '0 2px 10px rgba(216,90,48,0.05)',
    }}>
      <div style={{ fontSize: 12, color: MUTED, marginBottom: 6, whiteSpace: 'nowrap' }}>{title}</div>
      <div style={{ fontSize: 20, fontWeight: 800, color: color || INK, lineHeight: 1.2, whiteSpace: 'nowrap' }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: '#C4B3A8', marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

// 小指标格
function MiniStat({ title, value, color }) {
  return (
    <div style={{ background: '#FFFDFB', border: '1px solid ' + BORDER, borderRadius: 12, padding: '10px 12px', minWidth: 0 }}>
      <div style={{ fontSize: 11, color: MUTED, marginBottom: 4, whiteSpace: 'nowrap' }}>{title}</div>
      <div style={{ fontSize: 16, fontWeight: 700, color: color || INK, whiteSpace: 'nowrap' }}>{value}</div>
    </div>
  );
}

// 字段标签（自动=珊瑚点+「自动」徽章，手动=灰点）
function Field({ label, auto, children, span = 12 }) {
  return (
    <Col span={span}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 5 }}>
        <span style={{ width: 6, height: 6, borderRadius: 3, background: auto ? CORAL : '#D8C6BB', flexShrink: 0 }} />
        <span style={{ fontSize: 12, color: auto ? CORAL : '#8C7A70', fontWeight: 500, whiteSpace: 'nowrap' }}>{label}</span>
        {auto && <span style={{ fontSize: 10, color: CORAL, background: CORAL_BG, borderRadius: 4, padding: '0 5px', fontWeight: 600 }}>自动</span>}
      </div>
      {children}
    </Col>
  );
}

// 状态小药丸
function Pill({ ok, text }) {
  return (
    <span style={{
      fontSize: 12, fontWeight: 700, padding: '3px 12px', borderRadius: 99, whiteSpace: 'nowrap',
      background: ok ? '#E7F6EE' : '#FDECEA', color: ok ? '#237A46' : '#C22E1F',
    }}>{text}</span>
  );
}

// [v4] 自写 SVG 折线图——零依赖、零兼容问题
function SimpleLineChart({ data, paybackIdx, height = 280 }) {
  const W = 920, H = height;
  const padL = 56, padR = 16, padT = 18, padB = 28;
  const innerW = W - padL - padR, innerH = H - padT - padB;
  const n = data.length;
  if (n === 0) return <Empty description="暂无数据" />;
  const min = Math.min(0, ...data), max = Math.max(0, ...data);
  const range = max - min || 1;
  const pad = range * 0.08;
  const yMin = min - pad, yMax = max + pad, yRange = yMax - yMin;
  const x = (i) => padL + (n === 1 ? innerW / 2 : (i / (n - 1)) * innerW);
  const y = (v) => padT + (1 - (v - yMin) / yRange) * innerH;
  const pts = data.map((v, i) => `${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ');
  const baseY = y(0);
  const areaPath = `M ${x(0).toFixed(1)} ${baseY.toFixed(1)} L ${data.map((v, i) => `${x(i).toFixed(1)} ${y(v).toFixed(1)}`).join(' L ')} L ${x(n - 1).toFixed(1)} ${baseY.toFixed(1)} Z`;
  const zeroY = y(0);
  const ticks = [yMax, yMax - yRange / 2, 0, yMin + yRange / 2, yMin].filter((v, i, arr) => i === 0 || Math.abs(v) > 0.5 || i === arr.length - 1).slice(0, 5);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ width: '100%', height }}>
      {yMin < 0 && yMax > 0 && (
        <line x1={padL} y1={zeroY} x2={W - padR} y2={zeroY} stroke="#D8C6BB" strokeWidth={1} strokeDasharray="4 4" />
      )}
      {ticks.map((v, i) => (
        <g key={i}>
          <line x1={padL - 4} y1={y(v)} x2={padL} y2={y(v)} stroke="#C4B3A8" />
          <text x={padL - 8} y={y(v) + 3} textAnchor="end" fontSize={10} fill="#A08C82">¥{fmtMoney(v)}</text>
        </g>
      ))}
      {[0, Math.floor((n - 1) / 2), n - 1].map((i, k) => (
        <g key={k}>
          <line x1={x(i)} y1={H - padB} x2={x(i)} y2={H - padB + 4} stroke="#C4B3A8" />
          <text x={x(i)} y={H - padB + 16} textAnchor="middle" fontSize={10} fill="#A08C82">第 {i} 天</text>
        </g>
      ))}
      <path d={areaPath} fill={CORAL} fillOpacity={0.10} />
      <polyline points={pts} fill="none" stroke={CORAL} strokeWidth={2.5} strokeLinejoin="round" />
      <circle cx={x(n - 1)} cy={y(data[n - 1])} r={3.5} fill={CORAL} />
      {paybackIdx != null && paybackIdx < n && (
        <g>
          <line x1={x(paybackIdx)} y1={padT} x2={x(paybackIdx)} y2={H - padB} stroke={CORAL} strokeWidth={1.5} strokeDasharray="3 3" />
          <rect x={x(paybackIdx) - 32} y={padT + 4} width={64} height={20} rx={10} fill={CORAL} />
          <text x={x(paybackIdx)} y={padT + 18} textAnchor="middle" fontSize={11} fill="#fff" fontWeight={600}>回本 D{paybackIdx}</text>
        </g>
      )}
    </svg>
  );
}

// 根级错误边界：包整个 BlastPlan，任何渲染异常都暴露堆栈
function withErrBoundary(Wrapped) {
  return class extends React.Component {
    constructor(props) { super(props); this.state = { err: null }; }
    static getDerivedStateFromError(err) { return { err }; }
    componentDidCatch(err, info) {
      try { console.error('[BlastPlan root error]', err, info && info.componentStack); } catch (e) {}
    }
    render() {
      if (this.state.err) {
        return (
          <div style={{ padding: 16 }}>
            <Alert type="error" showIcon
              message="页面渲染失败（堆栈已捕获，请截图给开发定位）"
              description={<pre style={{ fontSize: 11, maxHeight: 360, overflow: 'auto', whiteSpace: 'pre-wrap', margin: 0 }}>{String((this.state.err && this.state.err.stack) || this.state.err)}</pre>} />
          </div>
        );
      }
      return <Wrapped {...this.props} />;
    }
  };
}

function BlastPlan() {
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null); // 当前选中的商品（受控 Select）
  const [metrics, setMetrics] = useState(null);
  const [loadingMetrics, setLoadingMetrics] = useState(false);
  // v7 默认参数：售价 125、货品成本 103、快递费 14.64（未达 60 单/天）；常规付费 5 元/单
  const [unit, setUnit] = useState({ price: 125, cost: 103, freight: 14.64, packaging: 0.5, platformRate: 1.2, refundRate: 5, resalable: 100, shipRecovery: 100 });
  const [opts, setOpts] = useState({ fixedCost: 4500, days: 30, targetDailyOrders: 60, stock: 30000, otherUpfront: 1000, cash: 150000, lagDays: 12 });
  const [stages, setStages] = useState([
    { name: '起量期(付费)', days: 30, dailyOrders: 40, adPer: 5 },
    { name: '稳定期(自然流)', days: 30, dailyOrders: 60, adPer: 0 },
  ]);
  const [stopTestStr, setStopTestStr] = useState('22,24,26,28,30');
  const [mix, setMix] = useState({ paid: 20, natural: 20, promoFee: 5 }); // v7 半付费混合记账
  // 项目保存/加载
  const [savedProjects, setSavedProjects] = useState([]);
  const [loadedProjectId, setLoadedProjectId] = useState(null);
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [tip, setTip] = useState('');
  const [formKey, setFormKey] = useState(0); // NumInput 非受控：加载商品/项目时 +1 强制重挂载同步显示

  const showTip = (t) => { setTip(t); setTimeout(() => setTip(''), 2600); };

  useEffect(() => {
    apiFetch('/api/blast/products').then(r => r.json()).then(d => { if (d.success) setProducts(d.data || []); }).catch(() => {});
    apiFetch('/api/blast/projects').then(r => r.json()).then(d => { if (d.success) setSavedProjects(d.data || []); }).catch(() => {});
  }, []);

  const loadMetrics = useCallback((name) => {
    if (!name) { setSelectedProduct(null); setMetrics(null); return; }
    setLoadingMetrics(true);
    apiFetch('/api/blast/metrics?name=' + encodeURIComponent(name)).then(r => r.json()).then(d => {
      setLoadingMetrics(false);
      if (!d.success || !d.data || !d.data.orderCount) return;
      const m = d.data;
      setSelectedProduct(name);
      setLoadedProjectId(null); // 选商品后清除已加载项目，避免状态冲突
      setMetrics(m);
      setUnit(u => ({ ...u, cost: m.avgCost || u.cost, packaging: m.avgPackaging || u.packaging }));
      setFormKey(k => k + 1);
    }).catch(() => setLoadingMetrics(false));
  }, []);

  // 加载已保存的项目：整套参数回填 + 输入框重挂载
  const loadProject = useCallback((id) => {
    apiFetch('/api/blast/projects/' + id).then(r => r.json()).then(d => {
      if (!d.success || !d.data) return;
      const p = d.data, dat = p.data || {};
      if (dat.unit) setUnit(dat.unit);
      if (dat.opts) setOpts(dat.opts);
      if (Array.isArray(dat.stages) && dat.stages.length) setStages(dat.stages);
      if (dat.stopTestStr != null) setStopTestStr(dat.stopTestStr);
      if (dat.mix) setMix(dat.mix);
      setLoadedProjectId(p.id);
      setSelectedProduct(null); // 加载项目后清除商品选择，避免状态冲突
      setMetrics({ name: p.productName || p.name, note: '已加载项目「' + p.name + '」，可继续修改后再保存' });
      setFormKey(k => k + 1);
      showTip('已加载「' + p.name + '」');
    }).catch(() => {});
  }, []);

  const openSave = () => {
    const loaded = savedProjects.find(p => String(p.id) === String(loadedProjectId));
    setSaveName(loaded ? loaded.name : (metrics && metrics.name ? metrics.name + ' 项目方案' : ''));
    setSaveOpen(true);
  };

  const doSave = () => {
    const name = saveName.trim();
    if (!name) return;
    const existing = savedProjects.find(p => p.name === name);
    apiFetch('/api/blast/projects', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: existing ? existing.id : undefined,
        name,
        productName: (metrics && metrics.name) || '',
        data: { unit, opts, stages, stopTestStr, mix },
      }),
    }).then(r => r.json()).then(d => {
      if (!d.success) { showTip(d.error || '保存失败'); return; }
      setSaveOpen(false);
      setLoadedProjectId(d.data.id);
      showTip(existing ? '已覆盖保存「' + name + '」' : '已保存「' + name + '」');
      apiFetch('/api/blast/projects').then(r => r.json()).then(dd => { if (dd.success) setSavedProjects(dd.data || []); }).catch(() => {});
    }).catch(() => showTip('保存失败'));
  };

  const delProject = () => {
    if (!loadedProjectId) return;
    const p = savedProjects.find(x => String(x.id) === String(loadedProjectId));
    apiFetch('/api/blast/projects/' + loadedProjectId, { method: 'DELETE' }).then(r => r.json()).then(d => {
      if (d.success) {
        setSavedProjects(prev => prev.filter(x => String(x.id) !== String(loadedProjectId)));
        setLoadedProjectId(null);
        showTip('已删除「' + (p ? p.name : '') + '」');
      }
    }).catch(() => {});
  };

  const plan = useMemo(() => computePlan(unit, opts, stages), [unit, opts, stages]);
  const scenarios = useMemo(() => [
    { label: '悲观（推广费 ×1.5）', ...scenario(unit, opts, stages, 1.5) },
    { label: '中性（当前假设）', ...scenario(unit, opts, stages, 1.0) },
    { label: '乐观（推广费 ×0.7）', ...scenario(unit, opts, stages, 0.7) },
  ], [unit, opts, stages]);
  const stopTest = useMemo(() => sevenDayTest(stopTestStr.split(/[,，、\s]+/), unit, opts), [stopTestStr, unit, opts]);
  const mixResult = useMemo(() => dailyMixResult(mix, unit, opts), [mix, unit, opts]);

  const setStage = (i, k, v) => setStages(prev => prev.map((s, idx) => idx === i ? { ...s, [k]: v } : s));
  const addStage = () => setStages(prev => [...prev, { name: '阶段' + (prev.length + 1), days: 15, dailyOrders: 30, adPer: 30 }]);
  const delStage = (i) => setStages(prev => prev.filter((_, idx) => idx !== i));
  const setOpt = (k, v) => setOpts(prev => ({ ...prev, [k]: v }));

  const verdict = useMemo(() => {
    // ⚠️ tone 只取 success/warning/error（TONE map 里没有的 key 会拿不到样式）
    if (plan.steadyNetPer <= 0) {
      return { tone: 'error', text: `守位期单笔净利为负（¥${fmt2(plan.steadyNetPer)}），每卖一单都在亏，任何投放都不可持续。建议先提价 / 降本 / 降运费，或核实「退货可再售 ρ」「运费申诉回收 σ」两项假设。` };
    }
    if (plan.paybackMonths == null) {
      return { tone: 'warning', text: `单笔虽正，但稳态月净现金流为负（¥${fmtMoney(plan.steadyMonthly)}），月固定成本未覆盖，无法回本。` };
    }
    return { tone: 'success', text: `模型可行：守位期单笔净利 ¥${fmt2(plan.steadyNetPer)}，稳态月净现金流 ¥${fmtMoney(plan.steadyMonthly)}，约 ${fmt2(plan.paybackMonths)} 个月回本。` };
  }, [plan]);

  const tv = TONE[verdict.tone] || TONE.warning;

  const breakdownRows = [
    { key: 'netIn', k: '净到手', v: '¥' + fmt2(plan.steadyNetIn) },
    { key: 'unitCost', k: '货品+运费成本', v: '−¥' + fmt2(plan.unitCost) },
    { key: 'refund', k: '退款残余损失', v: '−¥' + fmt2(plan.refundResidual) },
    { key: 'pack', k: '辅材', v: '−¥' + fmt2(plan.packaging) },
  ];
  const breakevenRows = [
    { key: 'maxAd', k: '保本单均推广费', v: plan.maxAdPer == null ? '—' : '¥' + fmt2(plan.maxAdPer) },
    { key: 'maxRate', k: '保本推广费率', v: fmtPct(plan.maxPromoRate) },
    { key: 'minPrice', k: '保本最低售价', v: plan.minPrice == null ? '—' : '¥' + fmt2(plan.minPrice) },
  ];

  const rowLine = (r, i, arr) => (
    <div key={r.key} style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      padding: '8px 2px', fontSize: 13, borderBottom: i < arr.length - 1 ? '1px dashed ' + BORDER : 'none',
    }}>
      <span style={{ color: '#8C7A70' }}>{r.k}</span><b style={{ color: INK }}>{r.v}</b>
    </div>
  );

  return (
    <div key={formKey} style={{ padding: '18px 20px', background: CREAM, minHeight: '100%' }}>
      {/* 页头 */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18, flexWrap: 'wrap', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{
            width: 40, height: 40, borderRadius: 13, background: 'linear-gradient(135deg,#E06B3F,#C94E26)',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 4px 12px rgba(216,90,48,0.35)',
          }}><ThunderboltOutlined style={{ color: '#fff', fontSize: 19 }} /></span>
          <div>
            <div style={{ fontSize: 18, fontWeight: 800, color: INK, lineHeight: 1.3 }}>
              项目预测
              <span style={{ fontSize: 11, fontWeight: 700, color: CORAL, background: CORAL_BG, borderRadius: 6, padding: '2px 7px', marginLeft: 8, verticalAlign: 2 }}>Accoi 模型 v7</span>
            </div>
            <div style={{ fontSize: 12, color: MUTED, marginTop: 2 }}>推广费 = 每单固定金额 · 只自动读「商品成本 + 辅材」，其余手动填 · 费率类按 % 填（如 1.2 / 5 / 100）</div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          {tip && (
            <span style={{ fontSize: 12, color: '#237A46', background: '#E7F6EE', border: '1px solid #BEE6CC', borderRadius: 99, padding: '4px 12px', fontWeight: 600 }}>{tip}</span>
          )}
          {metrics && metrics.note && (
            <span style={{ fontSize: 12, color: CORAL, background: CORAL_BG, border: '1px solid #F5CDB9', borderRadius: 99, padding: '4px 12px', fontWeight: 600 }}>{metrics.note}</span>
          )}
          <Button icon={<SaveOutlined />} onClick={openSave} style={{
            borderRadius: 10, fontWeight: 700, color: '#fff', border: 'none',
            background: 'linear-gradient(135deg,#E06B3F,#C94E26)', boxShadow: '0 3px 10px rgba(216,90,48,0.35)',
          }}>保存项目</Button>
        </div>
      </div>

      {/* ===== 结论总览（全宽 hero：结论横幅 + 四大核心指标） ===== */}
      <div style={{ background: '#fff', borderRadius: 16, padding: '16px 18px', marginBottom: 16, border: '1px solid ' + BORDER, boxShadow: '0 2px 12px rgba(216,90,48,0.06)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '12px 16px', borderRadius: 12, background: tv.bg, border: '1px solid ' + tv.bd, marginBottom: 14 }}>
          <span style={{ fontSize: 12, fontWeight: 800, color: tv.fg, background: tv.chipBg, borderRadius: 99, padding: '5px 14px', flexShrink: 0, border: '1px solid ' + tv.bd }}>结论 · {tv.chip}</span>
          <b style={{ fontSize: 14, color: tv.fg, lineHeight: 1.6 }}>{verdict.text}</b>
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          <StatCard title="守位期单笔净利" value={'¥' + fmt2(plan.steadyNetPer)} color={plan.steadyNetPer >= 0 ? GREEN : RED} sub="每单真正落袋" />
          <StatCard title="稳态月净现金流" value={'¥' + fmtMoney(plan.steadyMonthly)} color={plan.steadyMonthly >= 0 ? GREEN : RED} sub="已扣月固定成本" />
          <StatCard title="回本周期" value={plan.paybackMonths == null ? '无法回本' : r1(plan.paybackMonths) + ' 个月'} color={CORAL} sub="前期投入回收时间" />
          <StatCard title="保本单均推广费" value={plan.maxAdPer == null ? '—' : '¥' + fmt2(plan.maxAdPer)} sub="超过即单笔转亏" />
        </div>
      </div>

      <Row gutter={18}>
        {/* ============ 左：输入区 ============ */}
        <Col span={9}>
          <CandyCard no="1" title="选商品 / 项目" sub="自动读取成本价 + 辅材；已保存的项目可一键回填整套参数">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <Select showSearch allowClear placeholder="选商品（按真实订单聚合）" style={{ width: '100%' }} loading={loadingMetrics}
                value={selectedProduct}
                optionFilterProp="label"
                options={products.map(p => ({ value: p.name, label: `${p.name}（${p.orderCount}单）` }))}
                onSelect={loadMetrics}
                onClear={() => { setSelectedProduct(null); setMetrics(null); }} />
              <div style={{ display: 'flex', gap: 8 }}>
                <Select style={{ flex: 1 }} placeholder="加载已保存的项目" value={loadedProjectId}
                  onChange={(id) => loadProject(id)}
                  notFoundContent="还没有保存过项目"
                  options={savedProjects.map(p => ({ value: p.id, label: p.name + (p.productName ? '（' + p.productName + '）' : '') }))} />
                <Button danger disabled={!loadedProjectId} onClick={delProject} style={{ borderRadius: 9 }}>删除</Button>
              </div>
            </div>
          </CandyCard>

          <CandyCard no="2" title="单均经济" sub="珊瑚点 = 自动读，灰点 = 手动填">
            <Row gutter={[10, 12]}>
              <Field label="客单价 ¥"><NumInput value={unit.price} onChange={v => setUnit(u => ({ ...u, price: v }))} step={0.5} /></Field>
              <Field label="商品成本 ¥" auto><NumInput value={unit.cost} onChange={v => setUnit(u => ({ ...u, cost: v }))} step={0.5} /></Field>
              <Field label="运费 ¥"><NumInput value={unit.freight} onChange={v => setUnit(u => ({ ...u, freight: v }))} step={0.5} /></Field>
              <Field label="辅材 ¥" auto><NumInput value={unit.packaging} onChange={v => setUnit(u => ({ ...u, packaging: v }))} step={0.1} /></Field>
              <Field label="平台费率"><NumInput value={unit.platformRate} onChange={v => setUnit(u => ({ ...u, platformRate: v }))} step={0.1} addonAfter="%" /></Field>
              <Field label="签收退款率"><NumInput value={unit.refundRate} onChange={v => setUnit(u => ({ ...u, refundRate: v }))} step={0.5} addonAfter="%" /></Field>
              <Field label="退货可再售 ρ"><NumInput value={unit.resalable} onChange={v => setUnit(u => ({ ...u, resalable: v }))} step={5} addonAfter="%" /></Field>
              <Field label="运费申诉回收 σ"><NumInput value={unit.shipRecovery} onChange={v => setUnit(u => ({ ...u, shipRecovery: v }))} step={5} addonAfter="%" /></Field>
            </Row>
          </CandyCard>

          <CandyCard no="3" title="经营 + 资金" sub="满负荷口径的资金需求测算">
            <Row gutter={[10, 12]}>
              <Field label="月固定成本 ¥"><NumInput value={opts.fixedCost} onChange={v => setOpt('fixedCost', v)} step={100} /></Field>
              <Field label="月天数"><NumInput value={opts.days} onChange={v => setOpt('days', v)} min={1} addonAfter="天" /></Field>
              <Field label="头部日单量"><NumInput value={opts.targetDailyOrders} onChange={v => setOpt('targetDailyOrders', v)} min={1} addonAfter="单" /></Field>
              <Field label="回款滞后"><NumInput value={opts.lagDays} onChange={v => setOpt('lagDays', v)} min={0} addonAfter="天" /></Field>
              <Field label="备货投入 ¥"><NumInput value={opts.stock} onChange={v => setOpt('stock', v)} step={1000} /></Field>
              <Field label="其他前期投入 ¥"><NumInput value={opts.otherUpfront} onChange={v => setOpt('otherUpfront', v)} step={100} /></Field>
              <Field label="可投入资金 ¥" span={24}><NumInput value={opts.cash} onChange={v => setOpt('cash', v)} step={1000} /></Field>
            </Row>
            <div style={{ marginTop: 12, fontSize: 12, color: MUTED, background: '#FFFDFB', border: '1px dashed ' + BORDER, borderRadius: 10, padding: '8px 10px' }}>
              <InfoCircleOutlined style={{ color: CORAL, marginRight: 5 }} />
              需准备现金 = 备货+其他前期投入 + 满负荷日单量×(货+运费+辅材+推广)×回款滞后天数
            </div>
          </CandyCard>

          <CandyCard no="4" title="爬坡节奏" sub="填 0 = 自然流；运费留空 = 跟随上方全局运费"
            extra={<Button size="small" icon={<PlusOutlined />} onClick={addStage}
              style={{ borderRadius: 9, borderColor: '#E8A583', color: CORAL, fontWeight: 600 }}>加阶段</Button>}>
            <div style={{ display: 'flex', gap: 6, fontSize: 11, color: '#C4B3A8', marginBottom: 6, paddingRight: 30 }}>
              <span style={{ flex: 1, minWidth: 70 }}>阶段名</span>
              <span style={{ width: 66, textAlign: 'center' }}>天数</span>
              <span style={{ width: 76, textAlign: 'center' }}>日均单量</span>
              <span style={{ width: 92, textAlign: 'center' }}>推广费¥/单</span>
              <span style={{ width: 76, textAlign: 'center' }}>运费¥</span>
            </div>
            {stages.map((s, i) => (
              <div key={i} style={{
                display: 'flex', gap: 6, alignItems: 'center', marginBottom: 8,
                background: '#FFFDFB', border: '1px solid ' + BORDER, borderRadius: 12, padding: '8px 8px',
              }}>
                <Input style={{ flex: 1, minWidth: 70, borderRadius: 8 }} value={s.name} onChange={e => setStage(i, 'name', e.target.value)} placeholder="阶段名" />
                <NumInput value={s.days} onChange={v => setStage(i, 'days', v)} min={0} style={{ width: 66 }} />
                <NumInput value={s.dailyOrders} onChange={v => setStage(i, 'dailyOrders', v)} min={0} style={{ width: 76 }} />
                <NumInput value={s.adPer} onChange={v => setStage(i, 'adPer', v)} min={0} step={1} style={{ width: 92 }} />
                <NumInput value={s.freight} onChange={v => setStage(i, 'freight', v)} min={0} step={0.5} style={{ width: 76 }} placeholder={String(unit.freight)} />
                <Button size="small" type="text" danger icon={<DeleteOutlined />} onClick={() => delStage(i)} />
              </div>
            ))}
          </CandyCard>
        </Col>

        {/* ============ 右：结果区（拆解 → 验证工具 → 曲线 → 资金 → 敏感性） ============ */}
        <Col span={15}>
          {/* 单笔经济 + 保本生死线：左右对照（等高） */}
          <Row gutter={16} align="stretch">
            <Col span={12}>
              <CandyCard title="单笔经济拆解" sub="守位期口径 · 每单的钱去哪了" style={{ height: '100%' }}>
                {breakdownRows.map((r, i) => rowLine(r, i, breakdownRows))}
                <div style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  marginTop: 8, padding: '9px 12px', borderRadius: 10,
                  background: plan.steadyNetPer >= 0 ? '#F0FAF4' : '#FEF1F0',
                }}>
                  <b style={{ fontSize: 13, color: INK }}>单笔净利</b>
                  <b style={{ fontSize: 16, color: plan.steadyNetPer >= 0 ? GREEN : RED }}>¥{fmt2(plan.steadyNetPer)}</b>
                </div>
              </CandyCard>
            </Col>
            <Col span={12}>
              <CandyCard title="保本生死线" sub="推广费 / 售价的两条红线" style={{ height: '100%' }}>
                {breakevenRows.map((r, i) => rowLine(r, i, breakevenRows))}
                <div style={{ fontSize: 12, color: MUTED, marginTop: 10, lineHeight: 1.6, background: '#FFFDFB', border: '1px dashed ' + BORDER, borderRadius: 10, padding: '8px 10px' }}>
                  推广费超过「保本单均推广费」即单笔转亏；售价低于「保本最低售价」即亏损。
                </div>
              </CandyCard>
            </Col>
          </Row>

          {/* 7 天停投 + 半付费：左右对照（等高） */}
          <Row gutter={16} align="stretch">
            <Col span={12}>
              <CandyCard title="7 天停投测试" sub="付费点火 3 天后停投，验证自然流能否接住"
                extra={<Pill ok={stopTest.passed} text={stopTest.verdict} />} style={{ height: '100%' }}>
                <div style={{ fontSize: 12, color: MUTED, marginBottom: 5 }}>逐日填停投后自然单量（逗号分隔）</div>
                <Input value={stopTestStr} onChange={e => setStopTestStr(e.target.value)} placeholder="如 22,24,26,28,30" style={{ borderRadius: 9, marginBottom: 12 }} />
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
                  <MiniStat title="停投后日均自然单" value={fmt2(stopTest.avgDaily)} />
                  <MiniStat title="保本所需日均" value={stopTest.breakEvenDaily === Infinity ? '∞' : fmt2(stopTest.breakEvenDaily)} />
                  <MiniStat title="目标日均（含15%垫）" value={stopTest.targetDaily} color={CORAL} />
                  <MiniStat title="当前量所需最低售价" value={stopTest.minPriceForVolume == null ? '—' : '¥' + fmt2(stopTest.minPriceForVolume)} />
                </div>
                <div style={{
                  fontSize: 13, padding: '10px 12px', borderRadius: 10, lineHeight: 1.6,
                  background: stopTest.passed ? '#F0FAF4' : '#FFF9EC',
                  color: stopTest.passed ? '#237A46' : '#A06A00',
                  border: '1px solid ' + (stopTest.passed ? '#BEE6CC' : '#F2DBA6'),
                }}>{stopTest.suggestion}</div>
              </CandyCard>
            </Col>
            <Col span={12}>
              <CandyCard title="半付费混合记账" sub="每天的账：付费单扣推广费、自然单不扣"
                extra={<Pill ok={mixResult.net >= 0} text={mixResult.net >= 0 ? '当日盈利' : '当日亏损'} />} style={{ height: '100%' }}>
                <Row gutter={[8, 8]} style={{ marginBottom: 12 }}>
                  <Field label="付费单量 / 天" span={8}><NumInput value={mix.paid} onChange={v => setMix(m => ({ ...m, paid: v }))} min={0} /></Field>
                  <Field label="自然单量 / 天" span={8}><NumInput value={mix.natural} onChange={v => setMix(m => ({ ...m, natural: v }))} min={0} /></Field>
                  <Field label="推广费（仅付费单扣）" span={8}><NumInput value={mix.promoFee} onChange={v => setMix(m => ({ ...m, promoFee: v }))} min={0} /></Field>
                </Row>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
                  <MiniStat title="自然单单笔净利" value={'¥' + fmt2(mixResult.naturalNet)} color={mixResult.naturalNet >= 0 ? GREEN : RED} />
                  <MiniStat title="付费单单笔净利" value={'¥' + fmt2(mixResult.paidNet)} color={mixResult.paidNet >= 0 ? GREEN : RED} />
                  <MiniStat title="今日总单量" value={mixResult.totalOrders + ' 单'} />
                  <MiniStat title="今日净利（扣固定）" value={'¥' + fmt2(mixResult.net)} color={mixResult.net >= 0 ? GREEN : RED} />
                  <MiniStat title="保本所需自然单/天" value={mixResult.breakEvenNaturalDaily === Infinity ? '∞' : fmt2(mixResult.breakEvenNaturalDaily)} color={CORAL} />
                  <MiniStat title="离保本还差自然单" value={mixResult.naturalShortfall == null ? '—' : fmt2(mixResult.naturalShortfall)} color={(mixResult.naturalShortfall || 0) > 0 ? RED : GREEN} />
                </div>
              </CandyCard>
            </Col>
          </Row>

          <CandyCard title="累计现金流曲线" sub="含前期投入，0 线即回本点">
            {plan.cashPoints.length > 1
              ? <SimpleLineChart data={plan.cashPoints} paybackIdx={plan.paybackDays != null ? plan.paybackDays : null} height={240} />
              : <Empty description="请先设置爬坡阶段" />}
          </CandyCard>

          <CandyCard title="资金生命线" sub="满负荷口径：备货 + 回款在途垫资">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
              <MiniStat title="需准备现金" value={'¥' + fmtMoney(plan.cashNeeded)} color={CORAL} />
              <MiniStat title="可支配现金" value={'¥' + fmtMoney(plan.freeCash)} color={plan.freeCash >= 0 ? GREEN : RED} />
              <MiniStat title="在途垫资" value={'¥' + fmtMoney(plan.inTransit)} />
              <MiniStat title="现金见底" value={plan.daysToZero == null ? '不会见底' : Math.round(plan.daysToZero) + ' 天'} color={plan.daysToZero == null ? GREEN : RED} />
            </div>
          </CandyCard>

          <CandyCard title="三档敏感性" sub="只缩放各阶段「单均推广费」，其余参数不变">
            <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr 1fr 1fr', gap: '0 12px', fontSize: 13 }}>
              <div style={{ padding: '6px 2px', color: '#C4B3A8', fontSize: 12 }}>情景</div>
              <div style={{ padding: '6px 2px', color: '#C4B3A8', fontSize: 12, textAlign: 'right' }}>累计广告预算</div>
              <div style={{ padding: '6px 2px', color: '#C4B3A8', fontSize: 12, textAlign: 'right' }}>稳态月净现金流</div>
              <div style={{ padding: '6px 2px', color: '#C4B3A8', fontSize: 12, textAlign: 'right' }}>回本周期</div>
              {scenarios.map((s) => (
                <React.Fragment key={s.label}>
                  <div style={{ padding: '9px 2px', borderTop: '1px dashed ' + BORDER, fontWeight: 600, color: INK }}>{s.label}</div>
                  <div style={{ padding: '9px 2px', borderTop: '1px dashed ' + BORDER, textAlign: 'right', color: INK }}>¥{fmtMoney(s.totalAd)}</div>
                  <div style={{ padding: '9px 2px', borderTop: '1px dashed ' + BORDER, textAlign: 'right', fontWeight: 600, color: s.steadyMonthly >= 0 ? GREEN : RED }}>¥{fmtMoney(s.steadyMonthly)}</div>
                  <div style={{ padding: '9px 2px', borderTop: '1px dashed ' + BORDER, textAlign: 'right' }}>
                    {s.paybackMonths == null
                      ? <Pill ok={false} text="无法回本" />
                      : <span style={{ fontWeight: 700, color: s.paybackMonths < 6 ? '#237A46' : '#A06A00' }}>{r1(s.paybackMonths)} 个月</span>}
                  </div>
                </React.Fragment>
              ))}
            </div>
          </CandyCard>
        </Col>
      </Row>

      {/* 保存项目弹窗 */}
      <Modal title="保存项目" open={saveOpen} onOk={doSave} onCancel={() => setSaveOpen(false)}
        okText="保存" cancelText="取消" okButtonProps={{ style: { background: CORAL, borderColor: CORAL } }}>
        <div style={{ fontSize: 12, color: MUTED, marginBottom: 6 }}>项目名称（同名保存将覆盖原参数）</div>
        <Input value={saveName} onChange={e => setSaveName(e.target.value)} placeholder="如：一次性牙刷-冲头部方案" onPressEnter={doSave} style={{ borderRadius: 9 }} />
        {saveName.trim() && savedProjects.some(p => p.name === saveName.trim()) && (
          <div style={{ fontSize: 12, color: '#A06A00', marginTop: 8 }}>已存在同名项目，保存将覆盖。</div>
        )}
        <div style={{ fontSize: 12, color: MUTED, marginTop: 12, lineHeight: 1.7, background: '#FFFDFB', border: '1px dashed ' + BORDER, borderRadius: 10, padding: '8px 10px' }}>
          将保存整套参数：单均经济 8 项、经营+资金 7 项、爬坡节奏、停投测试单量、半付费记账。之后可在左上角「加载已保存的项目」一键回填。
        </div>
      </Modal>
    </div>
  );
}

const BlastPlanSafe = withErrBoundary(BlastPlan);
export default BlastPlanSafe;
